import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminGroupsAPI } from '../services/api';

/**
 * Hook per verificare i permessi dell'utente corrente, considerando l'impersonazione e i gruppi admin.
 * Gestisce in modo centralizzato:
 * - Super admin
 * - Admin per ruolo (role='admin')
 * - Admin di gruppo (membro di admin_group_members)
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const [isGroupAdmin, setIsGroupAdmin] = useState<boolean | null>(null);
  const [loadingGroupCheck, setLoadingGroupCheck] = useState(false);
  
  // Controlla se c'è impersonazione attiva (sincrono, senza chiamate API)
  const impersonatedUserId = localStorage.getItem('impersonated_user_id');
  const isImpersonating = !!impersonatedUserId;
  
  // Verifica se l'utente è membro di un gruppo admin
  useEffect(() => {
    let cancelled = false;
    
    const checkGroupAdmin = async () => {
      if (!user) {
        if (!cancelled) {
          setIsGroupAdmin(false);
          setLoadingGroupCheck(false);
        }
        return;
      }

      // Se è super admin, non serve controllare
      if (user.is_super_admin) {
        if (!cancelled) {
          setIsGroupAdmin(false);
          setLoadingGroupCheck(false);
        }
        return;
      }

      // Verifica sempre se è membro di un gruppo admin
      try {
        if (!cancelled) {
          setIsGroupAdmin(null);
          setLoadingGroupCheck(true);
        }
        const response = await adminGroupsAPI.isUserGroupAdmin();
        if (!cancelled) {
          setIsGroupAdmin(response.is_group_admin || false);
        }
      } catch (err) {
        if (!cancelled) setIsGroupAdmin(false);
      } finally {
        if (!cancelled) setLoadingGroupCheck(false);
      }
    };

    // Verifica solo quando cambia l'ID utente
    if (user?.id) {
      checkGroupAdmin();
    } else {
      setIsGroupAdmin(false);
      setLoadingGroupCheck(false);
    }

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Quando c'è impersonazione attiva, AuthContext dovrebbe aver già caricato
  // i dati dell'utente impersonato in `user`. Quindi `user.role` è il ruolo dell'utente impersonato.
  const effectiveRole = user?.role;
  
  // L'utente è admin se:
  // 1. Ha role='admin' (può essere admin normale o admin di gruppo)
  // 2. È membro di un gruppo admin (anche se non ha role='admin')
  // IMPORTANTE: Se ha role='admin', è sempre admin (anche se isGroupAdmin è ancora null o false)
  // Questo è importante perché quando si aggiunge un utente a un gruppo admin, il ruolo viene aggiornato ad 'admin'
  const isAdmin = effectiveRole === 'admin' || isGroupAdmin === true;
  const shouldWaitForGroupResolution = !!user && !user.is_super_admin && isGroupAdmin === null;
  const effectiveLoadingGroupCheck = loadingGroupCheck || shouldWaitForGroupResolution;
  
  // Durante l'impersonazione, NON mostrare mai i menu super admin
  // Anche se l'utente impersonato ha is_super_admin=true, durante l'impersonazione non è super admin
  const isSuperAdmin = !isImpersonating && (user?.is_super_admin === true);
  
  return {
    isAdmin,
    isSuperAdmin,
    isGroupAdmin: isGroupAdmin === true,
    isImpersonating,
    loadingGroupCheck: effectiveLoadingGroupCheck,
    user,
  };
};

